import React,{useState,useEffect,useRef} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiTarget,FiClock,FiZap}=FiIcons;

const GameScreen=({score,setScore,timeLeft,onGameEnd})=> {
const [characters,setCharacters]=useState([]);
const [popups,setPopups]=useState([]);
const [nextGoldenTime,setNextGoldenTime]=useState(15);// First golden at 15s (30-15=15)
const [speedMultiplier,setSpeedMultiplier]=useState(1);
const gameAreaRef=useRef(null);
const characterIdRef=useRef(0);
const popupIdRef=useRef(0);

// Base character types with increased speeds
const baseCharacterTypes={
goodLead: {emoji: '👻',points: 10,message: 'Great prospect!',color: 'text-green-400',glowColor: 'shadow-green-400',baseSpeed: 3}, // 50% faster than 2
badLead: {emoji: '🧟',points: -5,message: 'Wrong target!',color: 'text-red-400',glowColor: 'shadow-red-400',baseSpeed: 2.1}, // 40% faster than 1.5
goldenLead: {emoji: '✨',points: 25,message: 'Hot lead closed!',color: 'text-yellow-400',glowColor: 'shadow-yellow-400',baseSpeed: 4} // 60% faster than 2.5
};

// Get current speed based on difficulty progression
const getCurrentSpeed=(baseSpeed)=> {
return baseSpeed * speedMultiplier;
};

// Update speed multiplier based on time progression
useEffect(()=> {
if (timeLeft <=7) {// Final 7 seconds: 40% speed increase total (20% + 20%)
setSpeedMultiplier(1.4);
} else if (timeLeft <=15) {// 15-22 seconds: 20% speed increase
setSpeedMultiplier(1.2);
} else {// First 15 seconds: normal speed
setSpeedMultiplier(1);
}
},[timeLeft]);

// Spawn characters with increased frequency
useEffect(()=> {
const spawnInterval=setInterval(()=> {
if (timeLeft <=0) return;
const shouldSpawnGolden=timeLeft <=nextGoldenTime && nextGoldenTime > 0;
if (shouldSpawnGolden) {
spawnCharacter('goldenLead');
setNextGoldenTime(prev=> prev - 7.5);
} else {
// Spawn regular characters more frequently
const types=['goodLead','badLead'];
const randomType=types[Math.floor(Math.random() * types.length)];
spawnCharacter(randomType);
// 50% chance to spawn an additional character for increased density
if (Math.random() < 0.5) {
const secondType=types[Math.floor(Math.random() * types.length)];
setTimeout(()=> spawnCharacter(secondType),300);
}
}
},800 - Math.max(timeLeft * 10,300)); // Increased frequency: 50% faster spawning
return ()=> clearInterval(spawnInterval);
},[timeLeft,nextGoldenTime,speedMultiplier]);

// Move characters with current speed
useEffect(()=> {
const moveInterval=setInterval(()=> {
setCharacters(prev=> prev.map(char=> ({...char,x: char.x + char.speed})).filter(char=> char.x < window.innerWidth + 100));
},50);
return ()=> clearInterval(moveInterval);
},[]);

// Clean up popups
useEffect(()=> {
const cleanupInterval=setInterval(()=> {
setPopups(prev=> prev.filter(popup=> Date.now() - popup.timestamp < 2000));
},100);
return ()=> clearInterval(cleanupInterval);
},[]);

const spawnCharacter=(type)=> {
const baseConfig=baseCharacterTypes[type];
const character={
id: characterIdRef.current++,
type,
x: -80,
y: Math.random() * (window.innerHeight - 200) + 100,
baseSpeed: baseConfig.baseSpeed,
speed: getCurrentSpeed(baseConfig.baseSpeed) + Math.random() * 0.5,
...baseConfig
};
setCharacters(prev=> [...prev,character]);
};

const catchCharacter=(character)=> {
// Remove character
setCharacters(prev=> prev.filter(c=> c.id !==character.id));
// Update score
setScore(prev=> Math.max(0,prev + character.points));
// Show popup
const popup={
id: popupIdRef.current++,
x: character.x,
y: character.y,
message: character.message,
points: character.points,
color: character.color,
timestamp: Date.now()
};
setPopups(prev=> [...prev,popup]);
};

const getScoreColor=()=> {
if (score >=150) return 'text-yellow-400';
if (score >=100) return 'text-green-400';
if (score >=50) return 'text-blue-400';
return 'text-white';
};

const getTimeColor=()=> {
if (timeLeft <=5) return 'text-red-400';
if (timeLeft <=15) return 'text-orange-400';
return 'text-white';
};

const getDifficultyIndicator=()=> {
if (timeLeft <=7) return '🔥';
if (timeLeft <=15) return '⚡';
return '👻';
};

return (
<div ref={gameAreaRef} className="relative z-10 min-h-screen overflow-hidden">
{/* Game HUD with difficulty indicator */}
<div className="absolute top-4 left-4 right-4 flex justify-between items-center z-20">
<motion.div className={`bg-black bg-opacity-70 rounded-lg px-4 py-2 ${getScoreColor()}`}
animate={{scale: score > 0 ? [1,1.1,1] : 1}}
transition={{duration: 0.3}}
>
<div className="flex items-center gap-2">
<SafeIcon icon={FiTarget} />
<span className="text-xl font-bold">Score: {score}</span>
</div>
</motion.div>
<motion.div className={`bg-black bg-opacity-70 rounded-lg px-4 py-2 ${getTimeColor()}`}
animate={{scale: timeLeft <=5 ? [1,1.1,1] : 1}}
transition={{duration: 0.5,repeat: timeLeft <=5 ? Infinity : 0}}
>
<div className="flex items-center gap-2">
<SafeIcon icon={FiClock} />
<span className="text-xl font-bold">{timeLeft}s</span>
<span className="text-lg">{getDifficultyIndicator()}</span>
</div>
</motion.div>
</div>

{/* Speed indicator */}
{speedMultiplier > 1 && (
<motion.div
initial={{opacity: 0,y: -20}}
animate={{opacity: 1,y: 0}}
className="absolute top-20 left-1/2 transform -translate-x-1/2 z-20"
>
<div className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold text-sm">
SPEED BOOST {speedMultiplier===1.2 ? '⚡' : '🔥'} x{speedMultiplier}
</div>
</motion.div>
)}

{/* Background forest elements */}
<div className="absolute inset-0">
<div className="absolute top-10 left-10 text-6xl">🌙</div>
<div className="absolute top-20 right-20 text-4xl">🦇</div>
<div className="absolute bottom-20 left-20 text-5xl">🌲</div>
<div className="absolute bottom-10 right-10 text-5xl">🌲</div>
<div className="absolute top-1/2 left-1/4 text-3xl opacity-70">🕷️</div>
</div>

{/* Characters */}
<AnimatePresence>
{characters.map(character=> (
<motion.div
key={character.id}
initial={{x: -80,opacity: 0}}
animate={{x: character.x,opacity: 1}}
exit={{opacity: 0,scale: 0}}
className={`absolute cursor-pointer select-none ${character.color}`}
style={{left: character.x,top: character.y,filter: `drop-shadow(0 0 10px ${character.type==='goldenLead' ? '#fbbf24' : character.type==='goodLead' ? '#10b981' : '#ef4444'})`}}
onClick={()=> catchCharacter(character)}
whileHover={{scale: 1.2}}
whileTap={{scale: 0.8}}
>
<div className="text-6xl">
{character.emoji}
</div>
{character.type==='goldenLead' && (
<motion.div
className="absolute inset-0 text-6xl"
animate={{rotate: 360}}
transition={{duration: 2,repeat: Infinity,ease: "linear"}}
>
✨
</motion.div>
)}
</motion.div>
))}
</AnimatePresence>

{/* Score Popups */}
<AnimatePresence>
{popups.map(popup=> (
<motion.div
key={popup.id}
initial={{opacity: 1,y: 0,scale: 1}}
animate={{opacity: 0,y: -50,scale: 1.2}}
exit={{opacity: 0}}
transition={{duration: 2}}
className={`absolute pointer-events-none z-30 ${popup.color} font-bold`}
style={{left: popup.x,top: popup.y}}
>
<div className="bg-black bg-opacity-80 rounded-lg px-3 py-1 text-sm">
{popup.message}
</div>
<div className="text-center text-lg font-bold">
{popup.points > 0 ? '+' : ''}{popup.points}
</div>
</motion.div>
))}
</AnimatePresence>

{/* Golden lead notification */}
{timeLeft===nextGoldenTime + 1 && nextGoldenTime >=0 && (
<motion.div
initial={{opacity: 0,scale: 0.5}}
animate={{opacity: 1,scale: 1}}
exit={{opacity: 0}}
className="absolute top-1/4 left-1/2 transform -translate-x-1/2 z-30"
>
<div className="bg-yellow-500 text-black px-6 py-3 rounded-lg font-bold text-xl shadow-lg">
<div className="flex items-center gap-2">
<SafeIcon icon={FiZap} />
VIP Lead Incoming!
</div>
</div>
</motion.div>
)}

{/* Character count indicator */}
<div className="absolute bottom-4 right-4 bg-black bg-opacity-70 rounded-lg px-3 py-1 text-white text-sm">
Targets: {characters.length}
</div>
</div>
);
};

export default GameScreen;